<template>
    <Card>
        <div class="card-body">
            <div class="row">
                <div class="col-lg-3">
                    <FormGroup :label="$t('filterCard.labels.source_filter')">
                        <AppSelect
                            :model-value="sourceFilter.key"
                            :selected-value="sourceFilter.label"
                            :options="source_filter_options"
                            disabled
                            option-key="key"
                            option-value="label"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3" v-for="languageAlias in this.integration.language_aliases">
                    <FormGroup :label="`${$t('labels.name')} (${$t(`languages.${languageAlias}`)})`">
                        <Input
                            :model-value="filter?.names?.[languageAlias]"
                            @update:model-value="(name) => filter.names[languageAlias] = name"
                            :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.type')">
                        <Input
                            :model-value="$t(`filter_types.${filter.type}`)"
                            readonly
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.min_value')">
                        <Input
                            type="number"
                            v-model="filter.min_number_value"
                            readonly
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.max_value')">
                        <Input
                            type="number"
                            v-model="filter.max_number_value"
                            readonly
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.measurement')">
                        <Input
                            v-model="filter.measurement_unit"
                            :disabled="sourceFilter.key === FilterSourceEnum.SERVICE"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.default_value')">
                        <CatalogAttributeSelect
                            v-if="filter?.uuid && category"
                            :attribute="attributeForDefaultItems"
                            :integration="integration"
                            :category="category"
                            :model-value="filter.default_items ?? []"
                            @update:model-value="filter.default_items = $event"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-12">
                    <FormGroup :label="$t('labels.ai_description')">
                        <Input
                            v-model="filter.ai_description"
                            :maxLength="255"
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.required')" >
                        <AppCheckbox
                            v-model="filter.is_required"
                            disabled
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.custom_values')" >
                        <AppCheckbox
                            v-model="filter.allow_custom_values"
                            disabled
                        />
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.is_multiple')" >
                        <AppCheckbox v-model="filter.is_multiple" disabled/>
                    </FormGroup>
                </div>
                <div class="col-lg-3">
                    <FormGroup :label="$t('labels.ai_use_in_product_description')">
                        <AppCheckbox v-model="filter.ai_use_in_product_description"/>
                    </FormGroup>
                </div>
            </div>
        </div>
        <div v-if="!isSupplier" class="card-footer d-flex justify-content-end">
            <Button color="light" @click="remove" class="mr-2">
                {{ $t('buttons.delete') }}
            </Button>
            <Button color="indigo" @click="edit">
                {{ $t('buttons.edit') }}
            </Button>
        </div>
    </Card>
    <Card :title="$t('labels.filter_items')">
        <div v-if="!isSupplier" class="card-body">
            <router-link
                class="btn btn-link"
                :to="{
                    name: 'CreateIntegrationFilterItemCard',
                    params: {
                        categoryUuid: $route.params.categoryUuid,
                        filterUuid: $route.params.filterUuid
                }
            }">
                <i class="icon-plus3 mr-2"></i>
                {{ $t("buttons.create") }}
            </router-link>
        </div>
        <Table
            :handle="tableHandle"
            :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/filter-items`"
            :columns="filterItemsTable.columns"
            v-model:orders="filterItemsTable.orders"
            :has-checkbox="!isSupplier"
            @update:selectedValues="selectedFilterItems = $event"
        >
            <template v-if="!isSupplier" #actionDropdownItems>
                <DropdownItem @click="$refs.DeleteFilterItemsModal.show(selectedFilterItems)">{{ $t('buttons.delete') }}</DropdownItem>
            </template>
            <template #td-id="{row}">
                <AppNullableCell>
                    <template #default v-if="row.id">
                        <router-link
                            :to="{
                                name: 'EditIntegrationFilterItemCard',
                                params: {
                                    categoryUuid: row.integration_filter.integration_category_uuid,
                                    filterUuid: row.integration_filter_uuid	,
                                    filterItemUuid: row.uuid
                                }}">
                            {{ row.id }}
                        </router-link>
                    </template>
                </AppNullableCell>
            </template>
            <template #td-source_id="{row}">
                <AppNullableCell>
                    <template #default v-if="row.source_id">
                        {{ row.source_id }}
                    </template>
                </AppNullableCell>
            </template>
            <template #[`td-name_${lang}`]="{row}" v-for="lang in integration.language_aliases" :key="`name_${lang}`">
                <AppNullableCell>
                    <template #default v-if="row.names[lang]">
                        <span class="long-string" :title="row.names[lang]">{{ row.names[lang] }}</span>
                    </template>
                </AppNullableCell>
            </template>
        </Table>
    </Card>
    <DeleteFilterItemsModal ref="DeleteFilterItemsModal" @categories:deleted="tableHandle.fetchRows({ integration_filter_uuids: [this.$route.params.filterUuid] })"/>
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import Button from "../../../../components/new/button/AppButton.vue";
import Table from "../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../components/new/table/tableHandle";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import AppNullableCell from "../../../components/tables/AppNullableCell.vue";
import DropdownItem from "../../../components/btnDropdown/DropdownItem.vue";
import DeleteFilterItemsModal from "../modals/DeleteFilterItemsModal.vue";
import AppSelect from "../../../../components/new/form/select/AppSelect.vue";
import CatalogAttributeSelect from "./products/edit/CatalogAttributeSelect.vue";

import { httpClient } from "../../../../services/HttpClient";
import MessageNotificator from "../../../../services/MessageNotificator";
import { getServicesWhoNeedAdditionalRequest, IntegrationServiceEnum } from '../../../enums/IntegrationServiceEnum';
import { FilterSourceEnum } from '../../../enums/FilterSourceEnum';

export default {
    name: "EditFilterCard",
    components: {
        Card, FormGroup, Input, Button, Table, AppCheckbox, AppNullableCell,
        DropdownItem, DeleteFilterItemsModal, AppSelect, CatalogAttributeSelect
    },
    props: {
        integration: {
            type: Object
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            FilterSourceEnum,
            filter: {},
            category: null,
            filterItemsTable: {
                columns: [],
                orders: []
            },
            servicesWhoNeedAdditionalRequest: getServicesWhoNeedAdditionalRequest(),
            selectedFilterItems: [],
            sourceFilter: {
                key: FilterSourceEnum.CUSTOM,
                label: this.$t('filterCard.labels.custom')
            },
            source_filter_options: [
                {key: FilterSourceEnum.CUSTOM, label: this.$t('filterCard.labels.custom')},
                {key: FilterSourceEnum.SERVICE, label: this.$t('filterCard.labels.from_service')}
            ]
        }
    },
    created() {
        let languages = this.integration.language_aliases;

        let langFields = languages.map(lang => ({
            key: `name_${lang}`,
            name: this.$t(`table.titles.name`) + ` (${this.$t(`shortLang.${lang}`)})`,
            width: 'auto',
            type: 'string',
            order: true,
            orderColumn: `names->${lang}`
        }));

        this.filterItemsTable.columns = [
            {key: "id", name: this.$t('table.titles.id'), width: "auto", type: "string", order: true},
            {key: "source_id", name: this.$t('table.titles.externalId'), width: "auto", type: "string", order: true},
            ...langFields,
            {key: 'integration_product_items_count', name: this.$t('table.titles.publications'), width: 'auto', type: 'string', order: true}
        ];
    },
    methods: {
        async fetchCategory() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/categories/${this.$route.params.categoryUuid}`);

            this.category = response.data.data;
        },
        async fetchFilter() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                uuids: [this.$route.params.filterUuid],
                extra_fields: ['defaultItems']
            }).then((response) => {
                this.filter = response.data.data[0]
                this.sourceFilter = this.filter.source_id ? {
                    key: FilterSourceEnum.SERVICE,
                    label: this.$t('filterCard.labels.from_service')
                } : {
                    key: FilterSourceEnum.CUSTOM,
                    label: this.$t('filterCard.labels.custom')
                }
            })
        },
        async edit() {
            let requestData = {
                ai_description: this.filter.ai_description,
                ai_use_in_product_description: this.filter.ai_use_in_product_description,
                default_item_uuids: this.filter.default_items.map(item => item?.uuid)
            };

            if (this.sourceFilter.key !== FilterSourceEnum.SERVICE) {
                requestData.names = this.filter.names;
            }

            if (this.sourceFilter.key === FilterSourceEnum.CUSTOM) {
                requestData.measurement_unit = this.filter.measurement_unit;
            }

            await httpClient.patch(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters/${this.$route.params.filterUuid}`, requestData).then(() => {
                new MessageNotificator().success(this.$t("alerts.success"))
            })
        },
        remove() {
            new MessageNotificator().confirm(this.$t("alerts.confirm_delete"), () => {
                httpClient.delete(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters/${this.$route.params.filterUuid}`).then(() => {
                    new MessageNotificator().success(this.$t("alerts.success"))
                    this.$router.push({name: "EditCategoryCard"})
                })
            });
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        attributeForDefaultItems() {
            const attribute = { ...this.filter };

            delete attribute.default_items;

            return attribute;
        }
    },
    mounted() {
        this.fetchCategory()
        this.fetchFilter()
        this.tableHandle.fetchRows({
            integration_filter_uuids: [this.$route.params.filterUuid],
            extra_fields: ['productsCount']
        })
    }
}
</script>
